import { Col, Form, Image, Input, Modal, Row, Select, Timeline, message } from "antd";
import { UploadFile } from "antd/lib";
import { Rule } from "antd/lib/form";
import { issueApi } from "api/issue.api";
import { issueCategoryApi } from "api/issueCategory.api";
import { AppButton, AppTextArea } from "components/antd-ui";
import { MultiImageUpload } from "components/Upload/MultiImageUpload";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { Issue, IssueLog, IssueStatus, IssueStatusTrans } from "types/issue";
import "./IssueModal.scss";
import { IssueCategory } from "types/issueCategory";
import { Media } from "types/media";
import { $url } from "utils/url";
import dayjs from "dayjs";
import { MapPin } from "lucide-react";
const rules: Rule[] = [{ required: true }];

const ISSUE_STATUS_BANNER_MODIFIER: Record<IssueStatus, string> = {
  [IssueStatus.PENDING]: "issueStatusBanner--pending",
  [IssueStatus.PROCESSING]: "issueStatusBanner--processing",
  [IssueStatus.COMPLETED]: "issueStatusBanner--completed",
  [IssueStatus.REJECTED]: "issueStatusBanner--rejected",
};

export const IssueModal = observer(
  ({
    visible,
    issue,
    onClose,
    onSubmitOk,
  }: {
    visible: boolean;
    issue: Partial<Issue>;
    onClose: () => void;
    onSubmitOk: () => void;
  }) => {
    const [form] = Form.useForm<Issue>();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<IssueCategory[]>();
    const watchStatus = Form.useWatch("status", form);
    const [logs, setLogs] = useState<IssueLog[]>([]);

    const [reasonReject, setReasonReject] = useState("");
    const [noteComplete, setNoteComplete] = useState("");
    const [resolvedImages, setResolvedImages] = useState<Media[]>([]);

    const fetchCategories = async () => {
      const res = await issueCategoryApi.findAll();
      setCategories(
        res.data.issueCategories.map((item: IssueCategory) => ({
          id: item.id,
          name: item.name,
        })),
      );
    };

    useEffect(() => {
      fetchCategories();
    }, []);

    const fetchIssueDetail = async () => {
      const res = await issueApi.findOne(issue?.id || 0);

      const issueDetail = res.data;

      form.setFieldsValue({
        ...issueDetail,
        title: issueDetail?.title,
        description: issueDetail?.description,
        reporterName: issueDetail?.reporterName,
        reporterPhone: issueDetail?.reporterPhone,
        latitude: issueDetail?.latitude,
        longitude: issueDetail?.longitude,
        reportImages: JSON.parse((issueDetail?.reportImages as string) || "[]"),
        resolvedImages: JSON.parse((issueDetail?.resolvedImages as string) || "[]"),
        issueCategoryId: issueDetail?.issueCategory?.id,
        status: issueDetail?.status || IssueStatus.PENDING,
      });
    };

    const fetchLogs = async () => {
      const res = await issueApi.logs({ issueId: issue?.id || 0, limit: 20, page: 1 });
      setLogs(res.data.issueHistories);
    };

    useEffect(() => {
      if (visible && issue) {
        fetchIssueDetail();
        fetchLogs();
      }
    }, [visible, issue]);

    const imageListResolved = useMemo<UploadFile<any>[]>(() => {
      if (issue?.resolvedImages) {
        return JSON.parse((issue?.resolvedImages as string) || "[]").map((e: any) => ({
          name: e.name + "",
          url: e.url,
          uid: e.uid + "",
        }));
      }
      return [];
    }, [issue?.resolvedImages]);

    const statusForm = form.getFieldValue("status");
    const [modalRejectVisible, setModalRejectVisible] = useState(false);
    const [modalCompleteVisible, setModalCompleteVisible] = useState(false);

    const handleReject = async () => {
      if (!reasonReject) {
        message.error("Vui lòng nhập lý do từ chối");
        return;
      }

      try {
        setLoading(true);
        await issueApi.reject(issue?.id || 0, { reasonReject: reasonReject });
        message.success("Từ chối báo cáo thành công");

        onSubmitOk();

        fetchIssueDetail();
        fetchLogs();
        handleCloseReject();
      } catch (error) {
        message.error("Từ chối báo cáo thất bại");
      } finally {
        setLoading(false);
      }
    };

    const handleCloseReject = () => {
      setModalRejectVisible(false);
      setReasonReject("");
    };

    const handleCloseComplete = () => {
      setModalCompleteVisible(false);
      setNoteComplete("");
      setResolvedImages([]);
    };

    const handleProcessing = async () => {
      try {
        setLoading(true);

        await issueApi.processing(issue?.id || 0);
        message.success("Tiến hành xử lý thành công");

        fetchIssueDetail();
        fetchLogs();
        onSubmitOk();
      } catch (error) {
        message.error("Tiến hành xử lý thất bại");
      } finally {
        setLoading(false);
      }
    };

    const handleComplete = async () => {
      let imagesResolved = "";
      if (resolvedImages?.length > 0) {
        imagesResolved = JSON.stringify(resolvedImages);
      }

      try {
        setLoading(true);
        await issueApi.complete(issue?.id || 0, { note: noteComplete, resolvedImages: imagesResolved });
        message.success("Hoàn thành xử lý thành công");
        fetchIssueDetail();
        fetchLogs();
        handleCloseComplete();
        onSubmitOk();
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    return (
      <div>
        <Modal
          style={{ top: 15 }}
          onCancel={onClose}
          open={visible}
          title={<h1 className="text-xl font-bold text-center">{"Cập nhật báo cáo sự cố"}</h1>}
          footer={null}
          width={1200}
          className="app-form-modal issue-modal-logs"
        >
          <div className="app-form-modal__body">
            <section className="app-form-modal__section">
              <Form layout="vertical" form={form}>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Row gutter={[12, 12]}>
                      <Col span={24}>
                        <Form.Item label="Trạng thái" name="status">
                          <div
                            className={`issueStatusBanner ${ISSUE_STATUS_BANNER_MODIFIER[form.getFieldValue("status") as IssueStatus]}`}
                          >
                            <span className="issueStatusBanner__value">
                              {IssueStatusTrans[form.getFieldValue("status") as IssueStatus]?.label}
                            </span>
                          </div>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Danh mục" name="issueCategoryId">
                          <Select>
                            {categories?.map((e) => (
                              <Select.Option key={e.id} value={e.id}>
                                {e.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Tiêu đề" name="title" rules={rules}>
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item label="Mô tả" name="description" rules={rules}>
                          <AppTextArea placeholder="" rows={3} />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Tên người báo cáo" name="reporterName" rules={rules}>
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label="Số điện thoại"
                          name="reporterPhone"
                          rules={[
                            {
                              validator: (rule, value, callback) => {
                                if (!/^\d{10}$/.test(value)) {
                                  return Promise.reject("Số điện thoại không hợp lệ");
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label={
                            <>
                              Kinh độ
                              {form.getFieldValue("latitude") && form.getFieldValue("longitude") && (
                                <div className="ml-2">
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${form.getFieldValue("latitude")},${form.getFieldValue("longitude")}`}
                                    target="_blank"
                                  >
                                    <MapPin className="h-4 w-4" aria-hidden color="blue" />
                                  </a>
                                </div>
                              )}
                            </>
                          }
                          name="longitude"
                          rules={rules}
                        >
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          label={
                            <>
                              Vĩ độ
                              {form.getFieldValue("latitude") && form.getFieldValue("longitude") && (
                                <div className="ml-2">
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${form.getFieldValue("latitude")},${form.getFieldValue("longitude")}`}
                                    target="_blank"
                                  >
                                    <MapPin className="h-4 w-4" aria-hidden color="blue" />
                                  </a>
                                </div>
                              )}
                            </>
                          }
                          name="latitude"
                          rules={rules}
                        >
                          <Input placeholder="" />
                        </Form.Item>
                      </Col>

                      {watchStatus === IssueStatus.REJECTED && (
                        <Col span={12}>
                          <Form.Item label="Lý do từ chối" name="reason" rules={rules}>
                            <Input placeholder="" />
                          </Form.Item>
                        </Col>
                      )}

                      {watchStatus === IssueStatus.COMPLETED && (
                        <Col span={12}>
                          <Form.Item label="Ghi chú hoàn thành" name="note">
                            <AppTextArea placeholder="" rows={3} />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[12, 12]}>
                      <Col span={24}>
                        <Form.Item shouldUpdate={true}>
                          {() => {
                            return (
                              <Form.Item
                                label="Ảnh báo cáo"
                                tooltip={
                                  "Những ảnh này sẽ được hiển thị khi người dùng truy cập vào báo cáo sự cố"
                                }
                                name="reportImages"
                                style={{ marginBottom: 0 }}
                              >
                                {(form.getFieldValue("reportImages") as Media[])?.length > 0 ? (
                                  <div className="flex gap-2">
                                    {(form.getFieldValue("reportImages") as Media[])?.map((img) => (
                                      <Image
                                        src={img.url}
                                        alt={img.name}
                                        width={100}
                                        height={100}
                                        className="rounded-md shadow cursor-pointer object-contain border border-gray-200"
                                      />
                                    ))}
                                  </div>
                                ) : null}
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item shouldUpdate={true}>
                          {() => {
                            return (
                              <Form.Item
                                label="Ảnh giải quyết"
                                tooltip={
                                  "Những ảnh này sẽ được hiển thị khi người dùng truy cập vào báo cáo sự cố"
                                }
                                name="resolvedImages"
                                style={{ marginBottom: 0 }}
                              >
                                {(form.getFieldValue("resolvedImages") as Media[])?.length > 0 ? (
                                  <div className="flex gap-2">
                                    {(form.getFieldValue("resolvedImages") as Media[])?.map((img) => (
                                      <Image
                                        src={img.url}
                                        alt={img.name}
                                        width={100}
                                        height={100}
                                        className="rounded-md shadow cursor-pointer object-contain border border-gray-200"
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-500 italic">Chưa có ảnh giải quyết</span>
                                )}
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>

                      <Col span={14}>
                        <div className="font-semibold mb-4">Lịch sử báo cáo</div>
                        <Timeline
                          mode="left"
                          items={logs.map((log) => ({
                            label: dayjs(log.createdAt * 1000).format("HH:mm DD/MM/YYYY"),
                            children: log.note,
                          }))}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </section>
          </div>
          <div className="ant-modal-footer">
            <div className="flex gap-2 justify-end">
              <AppButton onClick={onClose}>Đóng</AppButton>

              {statusForm === IssueStatus.PENDING ? (
                <div className="flex gap-2">
                  <AppButton tone="danger" onClick={() => setModalRejectVisible(true)}>
                    Từ chối
                  </AppButton>

                  <AppButton loading={loading} tone="primary" onClick={handleProcessing}>
                    Tiến hành xử lý
                  </AppButton>
                </div>
              ) : null}

              {statusForm === IssueStatus.PROCESSING ? (
                <div className="flex gap-2">
                  <AppButton
                    loading={loading}
                    tone="primary"
                    onClick={() => setModalCompleteVisible(true)}
                    className="btn-completed"
                  >
                    Hoàn thành xử lý
                  </AppButton>
                </div>
              ) : null}
            </div>
          </div>
        </Modal>

        <Modal
          open={modalRejectVisible}
          onCancel={() => handleCloseReject()}
          title="Lí do từ chối"
          footer={null}
          centered
        >
          <AppTextArea
            placeholder=""
            rows={3}
            onChange={(e) => setReasonReject(e.target.value)}
            value={reasonReject}
          />

          <div className="mt-2 flex gap-2 justify-end">
            <AppButton tone="danger" onClick={() => handleCloseReject()}>
              Hủy
            </AppButton>
            <AppButton loading={loading} tone="primary" onClick={handleReject}>
              Xác nhận
            </AppButton>
          </div>
        </Modal>

        <Modal
          title="Xác nhận hoàn thành xử lý"
          open={modalCompleteVisible}
          onCancel={() => handleCloseComplete()}
          footer={null}
        >
          <div className="mb-2">
            Hệ thống yêu cầu bạn cập nhật <strong>Ảnh giải quyết</strong> thực tế trước khi chuyển trang thái
            sự cố này sang <strong>Hoàn thành</strong>
          </div>
          <MultiImageUpload
            multiple={true}
            onUploadOk2={(fileList) => {
              const filePayload =
                fileList.length > 0
                  ? fileList.map((item) => {
                      return {
                        url: $url(item.url) || $url(item.response?.data?.path),
                        name: item.name,
                        uid: item?.uid,
                      };
                    })
                  : [];

              setResolvedImages(filePayload as unknown as Media[]);
            }}
            fileListProp={imageListResolved}
            uploadUrl={import.meta.env.VITE_API_URL + "/v1/admin/issue/upload"}
          />

          <div className="my-2 font-semibold"> Ghi chú kết quả xử lý</div>
          <AppTextArea
            placeholder="Nhập ghi chú kết quả xử lý"
            rows={3}
            onChange={(e) => setNoteComplete(e.target.value)}
            value={noteComplete}
          />

          <div className="mt-2 flex gap-2 justify-end">
            <AppButton tone="danger" onClick={() => handleCloseComplete()}>
              Hủy
            </AppButton>
            <AppButton loading={loading} tone="primary" onClick={handleComplete}>
              Xác nhận
            </AppButton>
          </div>
        </Modal>
      </div>
    );
  },
);
